import { Button } from "@/common/elements/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/common/elements/sheet";
import {
  formatPaymentMethodSummary,
  UiPaymentMethodType,
  type UiPaymentMethod,
} from "@/plugins/controls/common/billing-mappers";
import { type MethodFormState } from "@/plugins/controls/payment-methods-control/utils";
import {
  CollectJsPaymentForm,
  type CollectJsTokenizationConfig,
  type CollectJsTokenizedPayment,
} from "@/plugins/controls/payment-methods-control/payment-methods-drawer/collect-js-payment-form/CollectJsPaymentForm";
import {
  type BillingAddressFormFields,
  type BillingAddressValidationErrors,
  type PaymentCountryOption,
  type PaymentStateOption,
} from "@/plugins/controls/common/billing-address"
import { BillingAddressFields } from "@/plugins/controls/common/billing-address-fields"
import { AchPaymentFields } from "@/plugins/controls/common/ach-payment-fields"

export enum PaymentMethodsDrawerMode {
  Create = "create",
  Edit = "edit",
}

type PaymentMethodsDrawerProps = {
  open: boolean;
  mode: PaymentMethodsDrawerMode;
  methodPreview: UiPaymentMethod | null;
  form: MethodFormState;
  collectJsConfig: CollectJsTokenizationConfig | null;
  collectTokenRequestNonce: number;
  collectDisabled?: boolean;
  collectError?: string | null;
  countryOptions: PaymentCountryOption[];
  stateOptions: PaymentStateOption[];
  validationErrors?: BillingAddressValidationErrors;
  isCountriesLoading?: boolean;
  isStatesLoading?: boolean;
  canDelete?: boolean;
  onOpenChange: (open: boolean) => void;
  onMethodTypeChange: (type: UiPaymentMethodType) => void;
  onFormChange: (nextValue: MethodFormState) => void;
  onCollectReadyChange: (ready: boolean) => void;
  onCollectCardCategoryChange?: (category: string | null) => void;
  surchargeNoticePercent?: number | null;
  showSurchargeNotice?: boolean;
  onCollectTokenized: (result: CollectJsTokenizedPayment) => void;
  onCollectError: (message: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete?: () => void;
};

export function PaymentMethodsDrawer({
  open,
  mode,
  methodPreview,
  form,
  collectJsConfig,
  collectTokenRequestNonce,
  collectDisabled = false,
  collectError,
  countryOptions,
  stateOptions,
  validationErrors,
  isCountriesLoading = false,
  isStatesLoading = false,
  canDelete = false,
  onOpenChange,
  onMethodTypeChange,
  onFormChange,
  onCollectReadyChange,
  onCollectCardCategoryChange,
  surchargeNoticePercent = null,
  showSurchargeNotice = false,
  onCollectTokenized,
  onCollectError,
  onSubmit,
  onCancel,
  onDelete,
}: PaymentMethodsDrawerProps) {
  const isCreateMode = mode === PaymentMethodsDrawerMode.Create;
  const shouldShowAchAuthorization =
    form.type === UiPaymentMethodType.Ach && (isCreateMode || form.isDefault);
  const achAuthorizationAction = isCreateMode
    ? "Add payment method"
    : "Save updates";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            {isCreateMode ? "Add payment method" : "Update payment method"}
          </SheetTitle>
          <SheetDescription>
            Manage AutoPay payment details without leaving the payment methods
            page.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-3 -mt-4 px-4 pb-4 md:grid-cols-2">
          {isCreateMode && (
            <>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                <span className="font-medium">Method type</span>
                <select
                  value={form.type}
                  onChange={(event) =>
                    onMethodTypeChange(
                      event.target.value as UiPaymentMethodType,
                    )
                  }
                  className="h-8 rounded-lg border border-input bg-background px-2.5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value={UiPaymentMethodType.Card}>Credit card</option>
                  <option value={UiPaymentMethodType.Ach}>
                    ACH bank account
                  </option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                <span className="font-medium">Nickname</span>
                <input
                  id="nickname"
                  value={form.nickname ?? ""}
                  onChange={(event) => {
                    onFormChange({
                      ...form,
                      nickname: event.target.value
                        ? event.target.value?.slice(0, 30)
                        : "",
                    });
                  }}
                  className="h-8 rounded-lg border border-input bg-background px-2.5 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
            </>
          )}

          {isCreateMode ? (
            <div className="md:col-span-2">
              <CollectJsPaymentForm
                paymentMethodType={form.type}
                config={collectJsConfig}
                tokenRequestNonce={collectTokenRequestNonce}
                onReadyChange={onCollectReadyChange}
                onCardCategoryChange={onCollectCardCategoryChange}
                onTokenized={onCollectTokenized}
                onError={onCollectError}
              />
              {form.type === UiPaymentMethodType.Ach ? (
                <div className="mt-3">
                  <AchPaymentFields
                    achAccountType={form.achAccountType}
                    achAccountHolderType={form.achAccountHolderType}
                    onChange={(nextValue) => onFormChange({ ...form, ...nextValue })}
                    accountTypeError={validationErrors?.achAccountType}
                    accountHolderTypeError={validationErrors?.achAccountHolderType}
                  />
                </div>
              ) : null}
              {showSurchargeNotice && surchargeNoticePercent !== null ? (
                <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  A credit card surcharge fee of {surchargeNoticePercent}% will
                  be applied when this payment method is charged.
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="rounded-md border bg-muted/20 p-3 text-sm md:col-span-2">
                <p className="font-medium">Saved payment method</p>
                <p className="mt-1 text-muted-foreground">
                  {formatPaymentMethodSummary(methodPreview)}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={form.disabled}
                  onChange={(event) =>
                    onFormChange({
                      ...form,
                      disabled: event.target.checked,
                      isDefault: event.target.checked ? false : form.isDefault,
                    })
                  }
                />
                Disable this payment method
              </label>
            </>
          )}

          {collectError ? (
            <p className="text-sm text-destructive md:col-span-2">
              {collectError}
            </p>
          ) : null}

          {isCreateMode ? (
            <>
              <div className="md:col-span-2">
                <BillingAddressFields
                  value={{
                    firstName: form.firstName,
                    lastName: form.lastName,
                    company: form.company,
                    address1: form.address1,
                    address2: form.address2,
                    city: form.city,
                    stateId: form.stateId,
                    postalCode: form.postalCode,
                    countryId: form.countryId,
                    phone: form.phone,
                    fax: form.fax,
                  }}
                  onChange={(nextValue: BillingAddressFormFields) =>
                    onFormChange({ ...form, ...nextValue })
                  }
                  validationErrors={validationErrors}
                  countryOptions={countryOptions}
                  stateOptions={stateOptions}
                  isCountriesLoading={isCountriesLoading}
                  isStatesLoading={isStatesLoading}
                />
              </div>
            </>
          ) : null}

          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(event) =>
                onFormChange({ ...form, isDefault: event.target.checked })
              }
              disabled={form.disabled}
            />
            Set as default AutoPay method
          </label>

          {shouldShowAchAuthorization ? (
            <p className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground md:col-span-2">
              {form.isDefault
                ? `By clicking "${achAuthorizationAction}," you authorize CloudFit Software, LLC to electronically debit the bank account you have provided for invoices due on your account, in the amount and on the due date shown on each invoice, and to re-initiate any debit returned for insufficient funds. This authorization remains in effect until you revoke it. You may revoke it at any time by either changing your payment method within the Portal or by contacting payments@cloudfitsoftware.com, provided we receive your request at least 2 business days before the next scheduled debit.`
                : `By clicking "${achAuthorizationAction}," you authorize CloudFit Software, LLC to electronically debit the bank account you have provided for invoices due on your account, in the amount of any invoice you use this payment method for.`}
            </p>
          ) : null}
        </div>

        <SheetFooter className="border-t">
          <Button onClick={onSubmit} disabled={collectDisabled}>
            {isCreateMode ? "Add payment method" : "Save updates"}
          </Button>
          {!isCreateMode && onDelete ? (
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={!canDelete}
            >
              Remove payment method
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
